import { TFunction } from 'i18next'
import { NavigateFunction } from 'react-router-dom'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { Eye } from 'lucide-react'
import EditIcons from '@/icons/EditIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    createColumns,
    DataTableColumn,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Department } from '../types/response'

// ============================================================================
// Column Definitions
// ============================================================================

export const getDepartmentListColumns = (
    t: TFunction
): DataTableColumn<Department>[] =>
    createColumns<Department>([
        {
            key: 'department_name',
            title: t('department_name'),
            width: 200,
            headerClassName: 'text-left',
            sortable: true,
            render: (value, row) => (
                <Link
                    to={generatePathDatacentral(paths.departmentDetail, {
                        id: row.department_id,
                    })}
                    className="text-primary hover:underline"
                >
                    {value}
                </Link>
            ),
        },
        {
            key: 'department_code',
            title: t('department_code'),
            width: 120,
            headerClassName: 'text-left',
            sortable: true,
        },
        {
            key: 'department_leader_name',
            title: t('department_leader'),
            width: 180,
            headerClassName: 'text-left',
            render: (value) => value || '-',
        },
        {
            key: 'keycloak_default_group_paths',
            title: 'Keycloak group',
            width: 160,
            headerClassName: 'text-left',
            render: (value) => value?.join(', ') || '-',
        },
        {
            key: 'department_description',
            title: t('description'),
            width: 250,
            headerClassName: 'text-left',
            cellClassName: 'truncate',
            render: (value) => value || '-',
        },
        {
            key: 'department_status',
            title: t('status'),
            width: 100,
            headerClassName: 'text-center',
            cellClassName: 'text-center',
            sortable: true,
            render: (value) => (
                <span
                    className={`px-2 py-1 rounded-full text-[10px] ${
                        value === 'active'
                            ? 'bg-green-100 text-c-success'
                            : 'bg-gray-100 text-gray-700'
                    }`}
                >
                    {value === 'active'
                        ? t('status_active')
                        : t('status_inactive')}
                </span>
            ),
        },
        {
            key: 'department_created_at',
            title: t('created_at'),
            width: 130,
            headerClassName: 'text-center',
            cellClassName: 'text-center',
            sortable: true,
            render: (value) =>
                value ? dayjs(value).format('DD/MM/YYYY') : '-',
        },
    ])

// ============================================================================
// Table Actions Config
// ============================================================================

interface ActionsParams {
    t: TFunction
    navigate: NavigateFunction
    setConfirmation: (config: any) => void
    setModal: (path: string, props: any) => void
    doDeleteDepartment: (id: number) => Promise<any>
    refetch: () => void
}

export const getDepartmentActions = ({
    t,
    navigate,
    setConfirmation,
    setModal,
    doDeleteDepartment,
    refetch,
}: ActionsParams): DataTableAction<Department>[] => [
    {
        key: 'view',
        label: t('view_detail'),
        icon: <Eye className="w-4 h-4" />,
        onClick: (row) =>
            navigate(
                generatePathDatacentral(paths.departmentDetail, {
                    id: row.department_id,
                })
            ),
    },
    {
        key: 'edit',
        label: t('edit'),
        icon: <EditIcons className="w-4 h-4" />,
        onClick: (row) =>
            setModal('DepartmentModals/Edit/index', {
                department: row,
                refetch,
            }),
    },
    {
        key: 'delete',
        label: t('delete'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        variant: 'danger',
        onClick: (row) =>
            setConfirmation({
                open: true,
                title: t('delete_department'),
                description: t('delete_department_confirm'),
                onConfirm: async () => {
                    await doDeleteDepartment(row.department_id)
                },
            }),
        hidden: (row) => row.department_status === 'active',
    },
]
